import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import { Button, Form, Input, Modal, Radio, Spin, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { globalModel } from "../../global/GlobalModel";
import { StoreOrderApi } from "./StoreOrderApi";
import type { AppMyStoreOrderRow, ListMyStoreOrdersResult } from "./StoreOrderType";
import "./MyOrdersPage.css";

const ID8 = /^\d{8}$/;

export const MyOrdersPanel = observer((props: { spaceId: string }) => {
  const { spaceId } = props;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AppMyStoreOrderRow[]>([]);
  const [detail, setDetail] = useState<AppMyStoreOrderRow | null>(null);
  const [afterOpen, setAfterOpen] = useState(false);
  const [afterForm] = Form.useForm<{
    afterType: "refund" | "extend_service";
    reason: string;
  }>();

  const load = useCallback(async () => {
    if (!spaceId || !globalModel.isLoggedIn) return;
    setLoading(true);
    const res = await StoreOrderApi.listMyOrders({
      spaceId,
      pageNum: 1,
      pageSize: 50,
    });
    setLoading(false);
    if (typeof res === "string") {
      return;
    }
    const data = res as ListMyStoreOrdersResult;
    setRows(data.list ?? []);
  }, [spaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = useCallback(
    async (orderId: string) => {
      const r = await StoreOrderApi.getMyOrderDetail({ spaceId, orderId });
      if (typeof r === "string") return;
      setDetail(r.order);
    },
    [spaceId]
  );

  const closeDetail = () => setDetail(null);

  const showAfter =
    detail &&
    detail.displayStatus !== "refunded" &&
    detail.displayStatus !== "completed" &&
    detail.displayStatus !== "unpaid";

  const submitAfter = async () => {
    if (!detail) return;
    try {
      const v = await afterForm.validateFields();
      const res = await StoreOrderApi.submitAfterSale({
        spaceId,
        orderId: detail.orderId,
        afterType: v.afterType,
        reason: v.reason.trim(),
      });
      if (typeof res === "string") return;
      message.success("已提交售后申请");
      setAfterOpen(false);
      afterForm.resetFields();
      closeDetail();
      void load();
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) return;
    }
  };

  const columns: ColumnsType<AppMyStoreOrderRow> = [
    { title: "订单号", dataIndex: "orderId", key: "orderId", ellipsis: true },
    {
      title: "状态",
      dataIndex: "displayStatusLabel",
      key: "displayStatusLabel",
      width: 100,
    },
    {
      title: "金额",
      key: "pay",
      width: 100,
      render: (_: unknown, r) =>
        r.displayStatus === "unpaid"
          ? `¥${r.productPriceYuan.toFixed(2)}`
          : `¥${r.payAmountYuan.toFixed(2)}`,
    },
    { title: "下单时间", dataIndex: "createdAt", key: "createdAt", width: 180 },
  ];

  if (!ID8.test(spaceId.trim())) {
    return <p className="my-orders-msg">无效的 spaceId</p>;
  }

  if (!globalModel.isLoggedIn) {
    return <p className="my-orders-msg">请先登录后查看订单</p>;
  }

  return (
    <div className="my-orders-inner my-orders-inner--embedded">
      <Spin spinning={loading}>
        <Table
          rowKey="orderId"
          columns={columns}
          dataSource={rows}
          pagination={false}
          onRow={(r) => ({
            onClick: () => void openDetail(r.orderId),
          })}
        />
      </Spin>

      <Modal
        title="订单详情"
        open={detail != null}
        onCancel={closeDetail}
        footer={null}
        width={520}
        destroyOnClose
        className="my-orders-modal"
      >
        {detail ? (
          <div className="my-orders-detail">
            <p>订单号：{detail.orderId}</p>
            <p>状态：{detail.displayStatusLabel}</p>
            <p>规格：{detail.serviceTier}</p>
            <p>金额：¥{detail.payAmountYuan.toFixed(2)}</p>
            <p>创建：{detail.createdAt}</p>
            {detail.payTime ? <p>支付：{detail.payTime}</p> : null}
            {showAfter ? (
              <div className="my-orders-after-wrap">
                <Button
                  type="default"
                  className="my-orders-after-btn"
                  onClick={() => {
                    afterForm.setFieldsValue({
                      afterType: "refund",
                      reason: "",
                    });
                    setAfterOpen(true);
                  }}
                >
                  申请售后
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>

      <Modal
        title="申请售后"
        open={afterOpen}
        onCancel={() => setAfterOpen(false)}
        onOk={() => void submitAfter()}
        okText="提交"
        cancelText="取消"
        destroyOnClose
        className="my-orders-modal"
      >
        <Form form={afterForm} layout="vertical">
          <Form.Item name="afterType" label={null} initialValue="refund">
            <Radio.Group>
              <Radio value="refund">我要退款</Radio>
              <Radio value="extend_service">我要补偿服务时间</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="reason"
            rules={[
              { required: true, message: "请填写原因" },
              { max: 300, message: "300字以内" },
            ]}
          >
            <Input.TextArea
              placeholder="原因描述（300字以内）"
              rows={4}
              maxLength={300}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});
